import * as React from "react";
import { cn } from "@/lib/utils";
import type { HeadingLevel } from "../../tokens";

const alignClasses = { left: "text-left", center: "text-center", right: "text-right" } as const;

/*
 * Fluid sizes from the type scale (phone → desktop). One class per level, so
 * a size utility in `className` (e.g. "text-gold-lg") reliably overrides it.
 */
const levelSizes: Record<HeadingLevel, string> = {
  h1: "text-[length:clamp(var(--font-size-3xl),3.5vw_+_1.75rem,var(--font-size-5xl))] tracking-wide",
  h2: "text-[length:clamp(var(--font-size-2xl),2.5vw_+_1.5rem,var(--font-size-4xl))] tracking-wide",
  h3: "text-[length:clamp(var(--font-size-xl),1.25vw_+_1.1rem,var(--font-size-2xl))]",
  h4: "text-[length:var(--font-size-xl)]",
  h5: "text-[length:var(--font-size-lg)]",
  h6: "text-[length:var(--font-size-sm)] uppercase tracking-[0.2em]",
};

const tones = {
  gold: "text-ds-gold-primary",
  gilded: "text-gilded",
  light: "text-ds-text-primary",
  bronze: "text-ds-bronze-light",
} as const;

export type HeadingTone = keyof typeof tones;

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Semantic level; also picks the size unless `size` is set. Default "h2". */
  level?: HeadingLevel;
  /** Visual size of another level, e.g. an h2 that looks like an h4. */
  size?: HeadingLevel;
  /** Default "gold". "gilded" is the animated-ready gold gradient. */
  tone?: HeadingTone;
  align?: "left" | "center" | "right";
}

/** Cinzel heading in gold with the right size for its level. */
export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ level = "h2", size, tone = "gold", align, className, ...props }, ref) => {
    const Component = level;
    return (
      <Component
        ref={ref}
        className={cn(
          "text-balance font-heading font-bold leading-[var(--line-height-tight)]",
          levelSizes[size ?? level],
          tones[tone],
          align && alignClasses[align],
          className,
        )}
        {...props}
      />
    );
  },
);
Heading.displayName = "Heading";
